import React, { useState } from 'react';
import { Icons } from '../components/ui/Icons.tsx';
import { api } from '../services/api';
import { MCP_BASE_URL } from '../config';

declare const process: any;

interface PricingPageProps {
  onClose: () => void;
  currentPlan?: 'free' | 'pro' | 'premium';
}

const PricingPage: React.FC<PricingPageProps> = ({ onClose, currentPlan = 'free' }) => {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [isProcessing, setIsProcessing] = useState(false);
  const [activePlan, setActivePlan] = useState<string>(currentPlan);
  const [selectedAddonCredits, setSelectedAddonCredits] = useState<number>(0);
  const [showAddonSelector, setShowAddonSelector] = useState(false);

  React.useEffect(() => {
      api.getUser().then(u => {
          if (u && u.plan) setActivePlan(u.plan.toLowerCase());
      }).catch(() => {});
  }, []);

  const handleUpgrade = async (selectedPlan: string) => {
      setIsProcessing(true);
      try {
          const session = await api.getAuthSession();
          if (!session) {
              alert("Please login first to upgrade.");
              return;
          }
          const token = session.access_token;
          
          const scriptLoaded = await new Promise((resolve) => {
              if ((window as any).Razorpay) return resolve(true);
              const script = document.createElement("script");
              script.src = "https://checkout.razorpay.com/v1/checkout.js";
              script.onload = () => resolve(true);
              script.onerror = () => resolve(false);
              document.body.appendChild(script);
          });
          
          if (!scriptLoaded) {
              alert("Payment SDK failed to load. Are you online?");
              return;
          }

          // Frontend-only exchange rate conversion:
          const pricesUsd: Record<string, Record<string, number>> = {
            pro: { monthly: 19, yearly: 180 },
            premium: { monthly: 199, yearly: 1908 }
          };
          const usdPrice = pricesUsd[selectedPlan]?.[billingCycle] || 19;
          
          let inrRate = 93.0;
          try {
            const exRes = await fetch("https://api.exchangerate-api.com/v4/latest/USD");
            if (exRes.ok) {
               const exData = await exRes.json();
               if (exData.rates?.INR) inrRate = exData.rates.INR;
            }
          } catch (e) {
            console.warn("Could not fetch exchange rate, using fallback.");
          }
          
          const inrAmount = Math.floor(usdPrice * inrRate * 100);
          
          // Get Razorpay Key from environment or fallback
          const RAZORPAY_KEY_ID = process.env.VITE_RAZORPAY_KEY_ID || (window as any).process?.env?.VITE_RAZORPAY_KEY_ID || "rzp_test_your_real_key";

          const options = {
              key: RAZORPAY_KEY_ID,
              amount: inrAmount,
              currency: "INR",
              name: "Neura AI",
              description: `Upgrade to ${selectedPlan.toUpperCase()} Plan (${billingCycle})`,
              handler: async function (response: any) {
                  try {
                      if (!response.razorpay_payment_id) {
                          alert("Payment failed or cancelled.");
                          return;
                      }

                      // Calculate billing dates
                      const now = new Date();
                      const expires = new Date();
                      if (billingCycle === 'yearly') {
                          expires.setFullYear(now.getFullYear() + 1);
                      } else {
                          expires.setMonth(now.getMonth() + 1);
                      }

                      // Update the plan directly in Supabase using the frontend api
                      const updatedUser = await api.updateUser({ 
                          plan: selectedPlan as any,
                          billing_cycle: billingCycle,
                          billed_at: now.toISOString(),
                          expires_at: expires.toISOString()
                      });
                      
                      if (updatedUser) {
                          alert(`Success! You have been upgraded to the ${selectedPlan.toUpperCase()} plan (${billingCycle})!`);
                          window.location.reload();
                      } else {
                          alert("Payment succeeded but plan update failed. Please contact support.");
                      }
                  } catch (e) {
                      console.error("Plan update error", e);
                      alert("Payment succeeded but error occurred during update. Please contact support.");
                  }
              },
              prefill: {
                  name: session.user?.user_metadata?.full_name || "",
                  email: session.user?.email || "",
              },
              theme: { color: "#000000" }
          };
          
          const rzp = new (window as any).Razorpay(options);
          rzp.on('payment.failed', function (response: any){
              alert("Payment Failed: " + response.error.description);
          });
          rzp.open();
          
      } catch (error: any) {
          console.error("Upgrade error:", error);
          alert(error.message || "An error occurred during upgrade.");
      } finally {
          setIsProcessing(false);
      }
  };

  const handleBuyAddon = async () => {
      if (selectedAddonCredits <= 0) return;
      setIsProcessing(true);
      try {
          const session = await api.getAuthSession();
          if (!session) {
              alert("Please login first to buy credits.");
              return;
          }
          
          const scriptLoaded = await new Promise((resolve) => {
              if ((window as any).Razorpay) return resolve(true);
              const script = document.createElement("script");
              script.src = "https://checkout.razorpay.com/v1/checkout.js";
              script.onload = () => resolve(true);
              script.onerror = () => resolve(false);
              document.body.appendChild(script);
          });
          
          if (!scriptLoaded) {
              alert("Payment SDK failed to load.");
              return;
          }

          const usdPrice = selectedAddonCredits / 100;
          let inrRate = 93.0;
          try {
            const exRes = await fetch("https://api.exchangerate-api.com/v4/latest/USD");
            if (exRes.ok) {
               const exData = await exRes.json();
               if (exData.rates?.INR) inrRate = exData.rates.INR;
            }
          } catch (e) {
            console.warn("Using fallback exchange rate.");
          }
          
          const inrAmount = Math.floor(usdPrice * inrRate * 100);
          const RAZORPAY_KEY_ID = process.env.VITE_RAZORPAY_KEY_ID || (window as any).process?.env?.VITE_RAZORPAY_KEY_ID || "rzp_test_your_real_key";

          const options = {
              key: RAZORPAY_KEY_ID,
              amount: inrAmount,
              currency: "INR",
              name: "Neura AI",
              description: `Purchase ${selectedAddonCredits} Add-on Credits`,
              handler: async function (response: any) {
                  try {
                      if (!response.razorpay_payment_id) {
                          alert("Payment failed.");
                          return;
                      }

                      const updatedUser = await api.addBonusCredits(session.user.id, selectedAddonCredits);
                      
                      if (updatedUser) {
                          alert(`Success! ${selectedAddonCredits} credits have been added to your account.`);
                          setSelectedAddonCredits(0);
                          window.location.reload();
                      }
                  } catch (e) {
                      console.error("Credit update error", e);
                      alert("Error updating credits. Please contact support.");
                  }
              },
              prefill: {
                  email: session.user?.email || "",
              },
              theme: { color: "#000000" }
          };
          
          const rzp = new (window as any).Razorpay(options);
          rzp.open();
          
      } catch (error: any) {
          console.error("Purchase error:", error);
          alert(error.message || "An error occurred.");
      } finally {
          setIsProcessing(false);
      }
  };

  const plans = {
    free: {
      monthly: { price: 'Free', sub: 'Includes' },
      yearly: { price: 'Free', sub: 'Includes' }
    },
    pro: {
      monthly: { price: '$19', sub: '/mo' },
      yearly: { price: '$15', sub: '/mo, billed yearly' }
    },
    premium: {
      monthly: { price: '$199', sub: '/mo' },
      yearly: { price: '$159', sub: '/mo, billed yearly' }
    }
  };

  return (
    <div className="w-full h-full flex flex-col bg-white dark:bg-[#050505] animate-in fade-in duration-500 overflow-y-auto no-scrollbar">
      {/* Navigation / Header Area */}
      <div className="flex items-center justify-between px-10 py-8">
        <div className="flex items-center gap-2 text-black dark:text-white">
           <span className="text-xl font-normal tracking-tight">Neura AI</span>
        </div>
        <button
          onClick={onClose}
          className="p-2 text-black dark:text-white hover:text-black dark:hover:text-white hover:bg-gray-100 dark:hover:bg-[#212121] rounded-full transition-all"
        >
          <Icons.X className="w-6 h-6" />
        </button>
      </div>

      <div className="max-w-7xl mx-auto px-6 pb-24 w-full">
        {/* Title Section */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-black dark:text-white mb-4 tracking-tight">Pricing</h1>
          <p className="text-xl text-black dark:text-gray-400">Choose the plan that works for you</p>
          
          <div className="mt-8 inline-flex items-center p-1 bg-gray-100 dark:bg-[#171717] rounded-xl relative">
             <button 
                onClick={() => setBillingCycle('monthly')}
                className={`px-6 py-2 rounded-lg text-sm font-bold transition-all ${billingCycle === 'monthly' ? 'bg-white dark:bg-[#212121] shadow-sm text-black dark:text-white' : 'text-black dark:text-gray-400 hover:text-black dark:hover:text-white'}`}
             >
                MONTHLY
             </button>
             <button 
                onClick={() => setBillingCycle('yearly')}
                className={`px-6 py-2 text-sm font-bold transition-all flex items-center gap-2 ${billingCycle === 'yearly' ? 'bg-white dark:bg-[#212121] shadow-sm text-black dark:text-white' : 'text-black dark:text-gray-400 hover:text-black dark:hover:text-white'}`}
             >
                YEARLY <span className="text-[10px] font-bold text-black dark:text-white bg-black/[0.06] dark:bg-white/[0.06] px-1.5 py-0.5 rounded">SAVE 20%</span>
             </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
          
          {/* Free Plan */}
          <div className="relative group bg-white dark:bg-[#212121] border border-black/[0.05] dark:border-gray-800 rounded-[24px] p-8 flex flex-col transition-all hover:border-black/[0.1] dark:hover:border-gray-700 hover:shadow-xl">
            <div className="flex items-center justify-between mb-8">
               <span className="text-lg font-medium text-black dark:text-white">Free</span>
            </div>
            <div className="mb-6">
               <h2 className="text-4xl font-bold text-black dark:text-white">Free</h2>
               <p className="text-sm text-black dark:text-gray-400 mt-2">Includes</p>
            </div>
            
            <ul className="space-y-4 mb-12 flex-1">
              <li className="flex items-start gap-3">
                 <Icons.Check className="w-4 h-4 text-black/20 dark:text-white/20 mt-1 flex-shrink-0" />
                 <span className="text-sm font-bold text-black dark:text-white">100 <span className="font-normal text-black dark:text-gray-400">starter credits</span></span>
              </li>
              <li className="flex items-start gap-3">
                 <Icons.Check className="w-4 h-4 text-black/20 dark:text-white/20 mt-1 flex-shrink-0" />
                 <span className="text-sm text-black dark:text-white font-medium">3 Active Integrations</span>
              </li>
              <li className="flex items-start gap-3">
                 <Icons.Check className="w-4 h-4 text-black/20 dark:text-white/20 mt-1 flex-shrink-0" />
                 <span className="text-sm text-black dark:text-white font-medium">No free Credits</span>
              </li>
            </ul>

            <button disabled={activePlan === 'free'} className={`w-full py-3.5 rounded-xl font-bold text-sm tracking-wide transition-all ${activePlan === 'free' ? 'bg-black/[0.05] dark:bg-white/[0.05] text-black/40 dark:text-white/40 cursor-default' : 'bg-white dark:bg-[#212121] text-black dark:text-white border border-black/[0.08] dark:border-gray-800 hover:border-black/[0.15] dark:hover:border-gray-750 hover:bg-black/[0.02] dark:hover:bg-gray-850'}`}>
               {activePlan === 'free' ? 'Current Plan' : 'Free Plan'}
            </button>
          </div>

          {/* Pro Plan */}
          <div className="relative group bg-white dark:bg-[#212121] border border-black/[0.1] dark:border-gray-800 rounded-[24px] p-8 flex flex-col transition-all hover:border-black/[0.15] dark:hover:border-gray-700 hover:shadow-xl shadow-[0_4px_40px_-10px_rgba(0,0,0,0.08)]">
            <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-black dark:bg-white text-white dark:text-black text-[9px] font-bold px-3.5 py-1 rounded-full uppercase tracking-[0.15em] shadow-md animate-pulse">
              Popular
            </span>
            <div className="flex items-center justify-between mb-8">
               <div className="flex gap-2">
                 <span className="text-lg font-medium text-black dark:text-white">Pro</span>
               </div>
            </div>
            <div className="mb-6">
               <div className="flex items-baseline">
                 <h2 className="text-4xl font-bold text-black dark:text-white">{plans.pro[billingCycle].price}</h2>
                 <span className="text-lg text-black dark:text-gray-400 ml-1 font-medium">{plans.pro[billingCycle].sub}</span>
               </div>
            </div>
            
            <ul className="space-y-4 mb-12 flex-1">
              <li className="flex items-start gap-3">
                 <Icons.Check className="w-4 h-4 text-black dark:text-white mt-1 flex-shrink-0" />
                 <span className="text-sm font-bold text-black dark:text-white">2,000 <span className="font-normal text-black dark:text-gray-400">credits per month</span></span>
              </li>
              <li className="flex items-start gap-3">
                 <Icons.Check className="w-4 h-4 text-black dark:text-white mt-1 flex-shrink-0" />
                 <span className="text-sm font-bold text-black dark:text-white">Unlimited Integrations</span>
              </li>
              <li className="flex items-start gap-3">
                 <Icons.Workflow className="w-4 h-4 text-black dark:text-white mt-1 flex-shrink-0" />
                 <span className="text-sm font-bold text-black dark:text-white">+1,000 <span className="font-normal text-black dark:text-gray-400">Welcome Bonus Credits</span></span>
              </li>
              <div className="ml-7">
                 <span className="text-[9px] font-bold bg-black dark:bg-white text-white dark:text-black px-2 py-0.5 rounded-sm uppercase tracking-widest shadow-sm">Limited Offer</span>
              </div>
            </ul>

            <button 
              onClick={() => handleUpgrade('pro')}
              disabled={isProcessing || activePlan === 'pro'}
              className={`w-full py-3.5 rounded-xl font-bold text-sm tracking-wide transition-all ${activePlan === 'pro' ? 'bg-black/[0.05] dark:bg-white/[0.05] text-black/40 dark:text-white/40 cursor-default' : 'bg-black dark:bg-white text-white dark:text-black hover:bg-black/90 dark:hover:bg-white/90 hover:scale-[1.01]'}`}>
               {activePlan === 'pro' ? 'Current Plan' : (isProcessing ? 'Processing...' : 'Upgrade')}
            </button>
          </div>

          {/* Premium Plan */}
          <div className="relative group bg-white dark:bg-[#212121] border border-black/[0.05] dark:border-gray-800 rounded-[24px] p-8 flex flex-col transition-all hover:border-black/[0.1] dark:hover:border-gray-700 hover:shadow-xl">
            <div className="flex items-center justify-between mb-8">
                <div className="flex gap-2">
                    <span className="text-lg font-medium text-black dark:text-white">Premium</span>
                </div>
            </div>
            <div className="mb-6">
               <div className="flex items-baseline">
                 <h2 className="text-4xl font-bold text-black dark:text-white">{plans.premium[billingCycle].price}</h2>
                 <span className="text-lg text-black dark:text-gray-400 ml-1 font-medium">{plans.premium[billingCycle].sub}</span>
               </div>
            </div>
            
            <ul className="space-y-4 mb-12 flex-1">
              <li className="flex items-start gap-3">
                 <Icons.Check className="w-4 h-4 text-black/20 dark:text-white/20 mt-1 flex-shrink-0" />
                 <span className="text-sm font-bold text-black dark:text-white">25,000 <span className="font-normal text-black dark:text-gray-400">credits per month</span></span>
              </li>
              <li className="flex items-start gap-3">
                 <Icons.Check className="w-4 h-4 text-black/20 dark:text-white/20 mt-1 flex-shrink-0" />
                 <span className="text-sm text-black dark:text-white font-bold">Unlimited Integrations</span>
              </li>
              <li className="flex items-start gap-3">
                 <Icons.Workflow className="w-4 h-4 text-black/20 dark:text-white/20 mt-1 flex-shrink-0" />
                 <span className="text-sm font-bold text-black dark:text-white">+5,000 <span className="font-normal text-black dark:text-gray-400">Welcome Bonus Credits</span></span>
              </li>
              <div className="ml-7">
                 <span className="text-[9px] font-bold bg-black/[0.05] dark:bg-white/[0.05] text-black/60 dark:text-white/60 px-2 py-0.5 rounded-sm uppercase tracking-widest shadow-sm">Limited Offer</span>
              </div>
            </ul>

            <button 
              onClick={() => handleUpgrade('premium')}
              disabled={isProcessing || activePlan === 'premium'}
              className={`w-full py-3.5 rounded-xl font-bold text-sm tracking-wide transition-all ${activePlan === 'premium' ? 'bg-black/[0.05] dark:bg-white/[0.05] text-black/40 dark:text-white/40 cursor-default' : 'bg-white dark:bg-[#212121] text-black dark:text-white border border-black/[0.08] dark:border-gray-800 hover:border-black/[0.15] dark:hover:border-gray-750 hover:bg-black/[0.02] dark:hover:bg-gray-850'}`}>
               {activePlan === 'premium' ? 'Current Plan' : (isProcessing ? 'Processing...' : 'Upgrade')}
            </button>
          </div>

          {/* Enterprise Plan */}
          <div className="relative group bg-white dark:bg-[#212121] border border-black/[0.05] dark:border-gray-800 rounded-[24px] p-8 flex flex-col transition-all hover:border-black/[0.1] dark:hover:border-gray-700 hover:shadow-xl">
            <div className="flex items-center justify-between mb-8">
               <span className="text-lg font-medium text-black dark:text-white">Enterprise</span>
            </div>
            <div className="mb-6">
               <h2 className="text-4xl font-bold text-black dark:text-white">Custom</h2>
            </div>
            
            <ul className="space-y-4 mb-12 flex-1">
              <li className="flex items-start gap-3">
                 <Icons.Check className="w-4 h-4 text-black/20 dark:text-white/20 mt-1 flex-shrink-0" />
                 <span className="text-sm text-black dark:text-white font-bold">Enterprise support</span>
              </li>
              <li className="flex items-start gap-3">
                 <Icons.Check className="w-4 h-4 text-black/20 dark:text-white/20 mt-1 flex-shrink-0" />
                 <span className="text-sm text-black dark:text-white font-bold">Bring your own API keys</span>
              </li>
              <li className="flex items-start gap-3">
                 <Icons.Check className="w-4 h-4 text-black/20 dark:text-white/20 mt-1 flex-shrink-0" />
                 <span className="text-sm text-black dark:text-white font-bold">Dedicated account manager</span>
              </li>
              <li className="flex items-start gap-3">
                 <Icons.Check className="w-4 h-4 text-black/20 dark:text-white/20 mt-1 flex-shrink-0" />
                 <span className="text-sm text-black dark:text-white font-bold">Custom contract & SLA</span>
              </li>
            </ul>

            <button className="w-full py-3.5 rounded-xl bg-white dark:bg-[#212121] text-black dark:text-white font-bold text-sm tracking-wide border border-black/[0.08] dark:border-gray-800 hover:border-black/[0.15] dark:hover:border-gray-700 hover:bg-black/[0.02] dark:hover:bg-gray-800 transition-all">
               Contact Sales
            </button>
          </div>

        </div>

        {/* Add-on Credits */}
        <div className="mt-24 max-w-3xl mx-auto">
           <div className="bg-white dark:bg-[#212121] border border-gray-100 dark:border-gray-800 rounded-[24px] p-10 text-center shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-gray-50 dark:bg-gray-850 rounded-2xl flex items-center justify-center mx-auto mb-6">
                 <Icons.Coins className="w-8 h-8 text-black dark:text-white" />
              </div>
              <h3 className="text-2xl font-bold text-black dark:text-white mb-2">Buy add-on credits</h3>
              <p className="text-black/60 dark:text-gray-400 text-sm mb-8">Purchase permanent credits that never expire. $1 per 100 credits.</p>
              
              {!showAddonSelector ? (
                  <button 
                    onClick={() => setShowAddonSelector(true)}
                    className="px-12 py-4 bg-black dark:bg-white text-white dark:text-black rounded-xl font-bold text-sm tracking-wide hover:scale-[1.02] transition-all"
                  >
                    Buy credits
                  </button>
              ) : (
                  <div className="flex flex-col items-center gap-8 animate-in slide-in-from-bottom-4 duration-500">
                      {/* Credit Selection */}
                      <div className="flex flex-wrap justify-center gap-4">
                          {[100, 200, 500].map((amount) => (
                              <button
                                  key={amount}
                                  onClick={() => setSelectedAddonCredits(prev => prev + amount)}
                                  className="px-6 py-3 rounded-xl border border-gray-200 dark:border-gray-800 hover:border-black dark:hover:border-white hover:bg-black dark:hover:bg-white hover:text-white dark:hover:text-black transition-all font-bold text-sm text-black dark:text-white"
                              >
                                  +{amount}
                              </button>
                          ))}
                          <button
                              onClick={() => setSelectedAddonCredits(0)}
                              className="px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800 text-black dark:text-white transition-all font-bold text-xs"
                          >
                              Reset
                          </button>
                          <button
                              onClick={() => {
                                  setSelectedAddonCredits(0);
                                  setShowAddonSelector(false);
                              }}
                              className="px-4 py-3 rounded-xl border border-red-50 dark:border-red-950/20 hover:bg-red-50 dark:hover:bg-red-950/30 text-red-500 dark:text-red-400 transition-all font-bold text-xs"
                          >
                              Cancel
                          </button>
                      </div>

                      {/* Summary & Buy Button */}
                      <div className="w-full max-w-md bg-gray-50 dark:bg-[#050505] rounded-2xl p-6 flex flex-col gap-4">
                          <div className="flex justify-between items-center text-sm font-medium">
                              <span className="text-black/60 dark:text-gray-400">Total Selected:</span>
                              <span className="text-black dark:text-white">{selectedAddonCredits} Credits</span>
                          </div>
                          <div className="flex justify-between items-center text-xl font-bold">
                              <span className="text-black dark:text-white">Price:</span>
                              <span className="text-black dark:text-white">${(selectedAddonCredits / 100).toFixed(2)} USD</span>
                          </div>
                          
                          <button 
                              disabled={selectedAddonCredits === 0 || isProcessing}
                              onClick={() => handleBuyAddon()}
                              className="w-full py-4 bg-black dark:bg-white text-white dark:text-black rounded-xl font-bold text-sm tracking-wide hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-30 disabled:hover:scale-100 shadow-xl shadow-black/10 dark:shadow-white/5"
                          >
                              {isProcessing ? 'Processing...' : `Purchase ${selectedAddonCredits} Credits`}
                          </button>
                      </div>
                  </div>
              )}
           </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
